/** Configs dos cadastros do grupo Gestão (Clientes, Locais, Frota, Pessoas, Ativos, Usuários). */
/* eslint-disable react-refresh/only-export-components -- configs co-locam os componentes Fields propositalmente */
import { z } from 'zod'
import { useWatch, useFormContext } from 'react-hook-form'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { ExternalLink, MapPin } from 'lucide-react'
import { useState } from 'react'
import { StatusChip } from '../../../shared/components/StatusChip'
import { PessoasProtheusBanner } from '../../../shared/protheus/ProtheusBanner'
import { FrotaBanner } from '../FrotaBanner'
import { CadastroStatusChip } from '../../../shared/components/CadastroStatusChip'
import { FleetIcon } from '../../../shared/components/FleetIcon'
import { ClassificacaoIcon } from '../../../shared/components/ClassificacaoIcon'
import {
  CATEGORIAS_COM_CLASSIFICACAO, CLASSIFICACOES_FROTA_LEVE, ROTULO_DA_CLASSIFICACAO, ehClassificacao,
  type ClassificacaoFrotaLeve,
} from '../../../shared/classificacaoFrotaLeve'
import { FLEET_ICON_SIZES } from '../../../shared/fleetTipos'
import { RHFText, RHFSelect, RHFSwitch, RHFDate, type Option } from '../../../shared/form/fields'
import { customerOptionsLoader, empresaPaiDoCliente } from '../../../shared/form/catalogLoaders'
import { CATALOG_STALE_TIME } from '../../../core/query/queryClient'
import { useAuth } from '../../../core/auth/useAuth'
import { useCan } from '../../../core/auth/useCan'
import { LocationMapLazy } from '../../mapa/LocationMapLazy'
import { parseCoord, isInRangeLatLng, isNullIsland, googleMapsUrl, roundCoord } from '../../mapa/coords'
import type { ResourceConfig, ResourceFilters } from '../config'
import {
  customersResource, usersResource, placesResource, fleetTodasDuplasResource, personnelTodasDuplasResource, assetsResource,
  placeCategoriesResource, fleetCategoriesResource, personnelCategoriesResource, assetCategoriesResource,
  feriadosResource, cargosResource,
} from '../resources'
import { useI18n } from '@/core/i18n/useI18n'
import { useCategoryOptions, makeCategoryFilter } from './_shared'
import { IdentidadeSection } from '../../usuarios/IdentidadeSection'
import { FuncaoHistorico } from '../FuncaoHistorico'
import {
  usuarioParaBody, usuarioParaForm, usuarioSchema, usuarioVazio,
  type UsuarioFormValues,
} from '../../usuarios/usuarioForm'
import type { Cargo, Cliente, Feriado, Fleet, Personnel, Place, Asset, Usuario, Role } from '../../../core/api/types'
import { RHFPermissionPicker } from '../../../shared/form/PermissionPicker'
import { RHFAreaPicker } from '../../../shared/form/AreaPicker'
import { AREAS } from '../../../core/areas/areas'
import { useDeclarativeText } from '../../../shared/i18n/declarativeText'
import { FormSection } from '../../../shared/form/FormSection'
import { fmtDate, fmtNum } from '../../../shared/format'
import { semPrefixoDaEmpresa } from '../../diarias/diariasShared'
import { CategoryLabel } from '../../../shared/components/CategoryLabel'
import { DeclarativeText } from '../../../shared/components/DeclarativeText'

const ATIVO = [{ value: 'active', label: 'Ativo' }, { value: 'inactive', label: 'Inativo' }]

/** Seletor de DONO (cliente) dos dados-mestre (Locais/Frota/Pessoas/Ativos).
 *  Só aparece para o staff — no portal do cliente o dono é implícito (o próprio) e
 *  o backend força `customer_id`, então o campo se auto-oculta. A lista de clientes
 *  é staff-only, então só busca quando é staff. */
function CustomerOwnerField() {
  const { user } = useAuth()
  const isStaff = Boolean(user?.is_staff)
  const customers = customersResource.useList({ per_page: 100 }, { staleTime: CATALOG_STALE_TIME, enabled: isStaff })
  if (!isStaff) return null
  const options: Option[] = [
    { value: '', label: '— Sem dono (global) —' },
    ...(customers.data?.items ?? []).map((c) => ({
      value: c.uuid, label: c.trade_name || c.legal_name, sublabel: empresaPaiDoCliente(c),
    })),
  ]
  return <RHFSelect name="customer_uuid" label="Cliente (dono)" loadOptions={customerOptionsLoader} selectedOptions={options} asyncKey="master-data:customers" placeholder="Buscar cliente…" />
}

// ── Clientes ─────────────────────────────────────────────────────────
const customerSchema = z.object({
  legal_name: z.string().min(1, 'Informe a razão social'),
  trade_name: z.string().optional(),
  cnpj: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  address_street: z.string().optional(), address_number: z.string().optional(),
  address_district: z.string().optional(), address_zip: z.string().optional(),
  city: z.string().optional(), uf: z.string().optional(),
  status: z.enum(['active', 'inactive']), notes: z.string().optional(),
})

function CustomerFields() {
  return (
    <>
      <RHFText name="legal_name" label="Razão social" required />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFText name="trade_name" label="Nome fantasia" />
        <RHFText name="cnpj" label="CNPJ" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFText name="email" label="E-mail" type="email" />
        <RHFText name="phone" label="Telefone" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 2 }}>
        <RHFText name="address_street" label="Logradouro" />
        <RHFText name="address_number" label="Número" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: 2 }}>
        <RHFText name="address_district" label="Bairro" />
        <RHFText name="city" label="Município" />
        <RHFText name="uf" label="UF" />
        <RHFText name="address_zip" label="CEP" />
      </Box>
      <RHFSelect name="status" label="Situação" options={ATIVO} />
      <RHFText name="notes" label="Observações" multiline rows={2} />
    </>
  )
}

export const customersConfig: ResourceConfig<Cliente> = {
  title: 'Clientes',
  singular: 'cliente', basePath: '/logistica/cadastros/customers', labelOf: (c) => c.trade_name || c.legal_name,
  exportPrefix: 'clientes',
  resource: customersResource, searchable: true, schema: customerSchema, Fields: CustomerFields,
  importEntity: 'customers', createPermission: 'master_data.create', editPermission: 'master_data.edit', deletePermission: 'master_data.delete',
  // As DUAS visões do cliente (Amorim, 01/09/2026): o Cliente é a filial
  // comercial, o nome fantasia que a operação usa — MONSANTO CAMPO VERDE — e a
  // Empresa pai é a razão social que agrupa as filiais — MONSANTO DO BRASIL
  // LTDA. A sincronização de subcontratos cria as filiais que faltarem.
  columns: [
    {
      key: 'cliente', header: 'Cliente', sortable: true,
      render: (c) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.trade_name || c.legal_name}</Typography>,
    },
    { key: 'legal_name', header: 'Empresa pai', sortable: true, render: (c) => (c.trade_name ? c.legal_name : dash) },
    { key: 'cnpj', header: 'CNPJ', sortable: true },
    { key: 'city', header: 'Município', render: (c) => c.city || '—' },
    { key: 'status', header: 'Situação', sortable: true, render: (c) => <StatusChip label={c.status === 'active' ? 'Ativo' : 'Inativo'} tone={c.status === 'active' ? 'success' : 'neutral'} /> },
  ],
  emptyForm: { legal_name: '', trade_name: '', cnpj: '', email: '', phone: '', address_street: '', address_number: '', address_district: '', address_zip: '', city: '', uf: '', status: 'active', notes: '' },
  toForm: (c) => ({
    legal_name: c.legal_name, trade_name: c.trade_name ?? '', cnpj: c.cnpj ?? '', email: c.email ?? '', phone: c.phone ?? '',
    address_street: c.address?.street ?? '', address_number: c.address?.number ?? '', address_district: c.address?.district ?? '', address_zip: c.address?.zip ?? '',
    city: c.city ?? '', uf: c.uf ?? '', status: c.status === 'inactive' ? 'inactive' : 'active', notes: c.notes ?? '',
  }),
  toBody: (v) => ({
    legal_name: v.legal_name, trade_name: v.trade_name || null, cnpj: v.cnpj || null, email: v.email || null, phone: v.phone || null,
    address_street: v.address_street || null, address_number: v.address_number || null, address_district: v.address_district || null, address_zip: v.address_zip || null,
    city: v.city || null, uf: v.uf ? String(v.uf).toUpperCase() : null, status: v.status, notes: v.notes || null,
  }),
}

// ── Locais ───────────────────────────────────────────────────────────
/** Campo de coordenada (string do input): vazio passa; preenchido tem que ser número
 *  na faixa geográfica. Vírgula/ponto e valores parciais são tratados pelo parseCoord. */
const coordField = (min: number, max: number, msg: string) =>
  z.string().optional().refine((v) => {
    if (!v) return true
    const n = parseCoord(v)
    return n != null && n >= min && n <= max
  }, msg)

const placeSchema = z.object({
  category: z.string().min(1, 'Selecione a categoria'), name: z.string().min(1, 'Informe o nome'),
  code: z.string().optional(), city: z.string().optional(), uf: z.string().optional(),
  latitude: coordField(-90, 90, 'Latitude deve estar entre -90 e 90'),
  longitude: coordField(-180, 180, 'Longitude deve estar entre -180 e 180'),
  geofence_type: z.enum(['circle', 'polygon']).default('circle'),
  geofence_polygon: z.array(z.tuple([z.number(), z.number()])).nullable().optional(),
  geofence_radius_km: z.string().optional()
    .refine((v) => !v || (Number(v) > 0 && Number(v) <= 50), 'Raio deve ser maior que 0 e no máximo 50'),
  address: z.string().optional(),
  contact: z.string().optional(), notes: z.string().optional(),
  // O link do local, fixo no cadastro (04/09/2026): o hotel da hospedagem do
  // maquinário lê daqui, em vez de o link ser digitado a cada frente.
  link: z.string().optional().refine((v) => !v || /^https?:\/\/\S+$/i.test(v), 'Informe um link começando com http'),
  // Config de hospedagem: só aparece (e só importa) quando a categoria é hotel.
  lodging_billing_mode: z.enum(['', 'semanal', 'quinzenal', 'mensal', 'final']).optional(),
  lodging_billing_notes: z.string().optional(),
  status: z.enum(['active', 'inactive']), customer_uuid: z.string().optional(),
}).superRefine((v, ctx) => {
  if (v.geofence_type === 'polygon') {
    // Polígono: a cerca vem do desenho no mapa (o centroide preenche lat/lng).
    if (!v.geofence_polygon || v.geofence_polygon.length < 3) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['geofence_polygon'], message: 'Desenhe a cerca no mapa (mínimo 3 pontos)' })
    }
    return
  }
  const hasLat = (v.latitude ?? '') !== ''
  const hasLng = (v.longitude ?? '') !== ''
  // Latitude e longitude andam em par: uma sem a outra não posiciona nada.
  if (hasLat !== hasLng) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [hasLat ? 'longitude' : 'latitude'], message: 'Informe latitude e longitude juntas' })
  }
  // 0, 0 (Ilha Nula) quase sempre é erro de preenchimento, nunca um local no Brasil.
  if (isNullIsland(parseCoord(v.latitude), parseCoord(v.longitude))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['latitude'], message: 'Latitude e longitude não podem ser ambas 0' })
  }
})

const FORMA_CERCA = [
  { value: 'circle', label: 'Círculo por ponto e raio' },
  { value: 'polygon', label: 'Polígono desenhado no mapa' },
]

const FORMA_COBRANCA_HOSPEDAGEM = [
  { value: 'semanal', label: 'Semanal' },
  { value: 'quinzenal', label: 'Quinzenal' },
  { value: 'mensal', label: 'Mensal' },
  { value: 'final', label: 'No final da hospedagem' },
]

function PlaceFields() {
  const tt = useDeclarativeText()
  const categoryOptions = useCategoryOptions(placeCategoriesResource)
  const geoType = (useWatch({ name: 'geofence_type' }) as string) || 'circle'
  const isPolygon = geoType === 'polygon'
  // Campos de hospedagem só quando a categoria é Hotel (mesmo padrão condicional
  // do geofence_type). A cobrança também é editável no módulo de Hospedagem.
  const isHotel = (useWatch({ name: 'category' }) as string) === 'hotel'
  return (
    <>
      <FormSection title={tt('Identificação')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
          <RHFSelect name="category" label="Categoria" required options={categoryOptions} />
          <RHFText name="code" label="Código" />
        </Box>
        <RHFText name="name" label="Nome" required />
      </FormSection>
      <FormSection title={tt('Localização e cerca')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
          <RHFText name="city" label="Município" />
          <RHFText name="uf" label="UF" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
          <RHFSelect name="geofence_type" label="Forma da cerca" options={FORMA_CERCA} />
          {isPolygon
            ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {tt('Desenhe a cerca no mapa abaixo. O centro é calculado sozinho.')}
                </Typography>
              </Box>
            )
            : <RHFText name="geofence_radius_km" label="Raio da cerca em km" type="number" placeholder="Padrão 1 km" />}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <RHFText name="latitude" label={isPolygon ? 'Latitude do centro' : 'Latitude'} type="number" disabled={isPolygon} />
          <RHFText name="longitude" label={isPolygon ? 'Longitude do centro' : 'Longitude'} type="number" disabled={isPolygon} />
        </Box>
      </FormSection>
      <FormSection title={tt('Contato e acesso')}>
        <RHFText name="address" label="Endereço" multiline rows={2} />
        <RHFText name="contact" label="Contato" />
        <RHFText name="link" label="Link do local" placeholder="https://…" />
      </FormSection>
      {isHotel && (
        <FormSection title={tt('Hospedagem')}>
          <RHFSelect name="lodging_billing_mode" label="Forma padrão de cobrança da hospedagem" options={FORMA_COBRANCA_HOSPEDAGEM} />
          <RHFText
            name="lodging_billing_notes" label="Observações comerciais da cobrança" multiline rows={2}
            placeholder="Data de fechamento, vencimento, proporcionalidade em saída antecipada e outras regras negociadas"
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
            {tt('A forma de cobrança é copiada para cada novo registro de hospedagem na criação. Registros existentes não mudam.')}
          </Typography>
        </FormSection>
      )}
      <FormSection title={tt('Complementos')}>
        <RHFSelect name="status" label="Situação" options={ATIVO} />
        <RHFText name="notes" label="Observações gerais" multiline rows={2} />
        <CustomerOwnerField />
      </FormSection>
    </>
  )
}

/** Card em largura total (abaixo do formulário) com o local no NOSSO mapa, no mesmo
 *  padrão visual do "Fazenda no mapa" do contrato. Clicar no mapa marca latitude e
 *  longitude, arrastar o marcador reposiciona, e o círculo mostra o raio da cerca ao
 *  vivo. Os campos de latitude/longitude seguem no formulário como alternativa e
 *  conferência; digitar coordenadas válidas move o marcador. */
function PlaceLocationMapCard() {
  const tt = useDeclarativeText()
  const values = useWatch({ name: ['latitude', 'longitude', 'category', 'geofence_radius_km', 'name', 'geofence_type', 'geofence_polygon'] }) as
    [string, string, string, string, string, string, [number, number][] | null | undefined]
  const [latStr, lngStr, category, raioStr, name, geoType, polygon] = values
  const { setValue } = useFormContext()
  const lat = parseCoord(latStr)
  const lng = parseCoord(lngStr)
  const isPolygon = geoType === 'polygon'
  const posicionado = isInRangeLatLng(lat, lng)
  const preenchido = latStr !== '' || lngStr !== ''
  const temPoligono = Array.isArray(polygon) && polygon.length >= 3

  function setCoord(nextLat: number | null, nextLng: number | null) {
    const opts = { shouldDirty: true, shouldValidate: true }
    setValue('latitude', nextLat != null ? String(nextLat) : '', opts)
    setValue('longitude', nextLng != null ? String(nextLng) : '', opts)
  }

  // Desenho do polígono: guarda o anel e deriva o centroide em latitude/longitude
  // (marcador/link/fitBounds continuam por ponto). Limpar volta tudo a vazio.
  function setPolygon(ring: [number, number][] | null) {
    const opts = { shouldDirty: true, shouldValidate: true }
    setValue('geofence_polygon', ring, opts)
    if (ring && ring.length >= 3) {
      const cx = ring.reduce((s, [lngV]) => s + lngV, 0) / ring.length
      const cy = ring.reduce((s, [, latV]) => s + latV, 0) / ring.length
      setCoord(roundCoord(cy), roundCoord(cx))
    } else {
      setCoord(null, null)
    }
  }

  return (
    <Card sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{tt(isPolygon ? 'Cerca no mapa' : 'Localização no mapa')}</Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {name?.trim() || tt('Novo local')}
            {posicionado && (
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {' · '}{lat!.toFixed(6)}, {lng!.toFixed(6)}
              </Box>
            )}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {isPolygon
              ? tt('Desenhe o polígono da cerca com o botão de polígono e clique no mapa.')
              : tt('Clique no mapa para marcar a posição, ou arraste o marcador.')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {posicionado && (
            <Button
              component="a"
              href={googleMapsUrl(lat!, lng!)}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ExternalLink size={15} />}
              size="small"
              color="inherit"
            >
              {tt('Abrir no Google Maps')}
            </Button>
          )}
          {isPolygon && temPoligono && (
            <Button size="small" color="inherit" onClick={() => setPolygon(null)}>
              {tt('Limpar cerca')}
            </Button>
          )}
          {!isPolygon && preenchido && (
            <Button size="small" color="inherit" onClick={() => setCoord(null, null)}>
              {tt('Limpar posição')}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ height: { xs: 300, sm: 360 } }}>
        <LocationMapLazy
          key={isPolygon ? 'polygon' : 'point'}
          latitude={lat}
          longitude={lng}
          categoryCode={category}
          radiusKm={parseCoord(raioStr)}
          drawMode={isPolygon ? 'polygon' : 'point'}
          polygon={isPolygon ? (polygon ?? null) : null}
          onPolygonChange={isPolygon ? setPolygon : undefined}
          onPick={isPolygon ? undefined : ((pickedLat, pickedLng) => setCoord(pickedLat, pickedLng))}
          height="100%"
        />
      </Box>
    </Card>
  )
}

const dash = <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
const fmtHa = (n?: number | null) =>
  n ? `${fmtNum(n)} ha` : null

/** Célula "Mapa": botão que abre o local no NOSSO mapa (modal) + link Google Maps. */
function PlaceMapCell({ place }: { place: Place }) {
  const [open, setOpen] = useState(false)
  const tt = useDeclarativeText()
  if (place.latitude == null || place.longitude == null) return dash
  const { latitude: lat, longitude: lng } = place
  return (
    <>
      <Tooltip title={tt('Ver no mapa')}>
        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setOpen(true) }}>
          <MapPin size={16} />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle sx={{ pb: 0.5 }}>{place.name}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {lat.toFixed(5)}, {lng.toFixed(5)}{place.city ? ` · ${place.city}${place.uf ? '/' + place.uf : ''}` : ''}
          </Typography>
          <Box sx={{ mt: 1.5, height: 340 }}>
            <LocationMapLazy
              latitude={lat}
              longitude={lng}
              categoryCode={place.category?.code}
              radiusKm={place.geofence_radius_km}
              polygon={place.geofence_type === 'polygon' ? (place.geofence_polygon ?? null) : null}
              height="100%"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button component="a" href={googleMapsUrl(lat, lng)} target="_blank" rel="noopener">
            {tt('Abrir no Google Maps')}
          </Button>
          <Button onClick={() => setOpen(false)} variant="contained">{tt('Fechar')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const placesConfig: ResourceConfig<Place> = {
  title: 'Locais', singular: 'local', exportPrefix: 'locais',
  basePath: '/logistica/cadastros/places', labelOf: (p) => p.name, resource: placesResource, searchable: true,
  schema: placeSchema, Fields: PlaceFields, Below: PlaceLocationMapCard, categoryNewPath: '/logistica/categorias/places/new',
  importEntity: 'places', createPermission: 'master_data.create', editPermission: 'master_data.edit', deletePermission: 'master_data.delete',
  columns: [
    {
      key: 'name', header: 'Local', sortable: true,
      render: (p) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{p.name}</Typography>
          {p.code ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.code}</Typography> : null}
        </Box>
      ),
    },
    { key: 'category', header: 'Categoria', render: (p) => p.category ? <CategoryLabel category={p.category} /> : dash },
    { key: 'city', header: 'Município/UF', render: (p) => (p.city ? `${p.city}${p.uf ? '/' + p.uf : ''}` : dash) },
    { key: 'hectares', header: 'Área', align: 'right', render: (p) => fmtHa(p.hectares) ?? dash },
    { key: 'contracts_count', header: 'Pivôs', align: 'right', render: (p) => p.contracts_count || dash },
    { key: 'map', header: 'Mapa', align: 'center', render: (p) => <PlaceMapCell place={p} /> },
    { key: 'status', header: 'Situação', sortable: true, render: (p) => <CadastroStatusChip status={p.status} /> },
  ],
  emptyForm: { category: '', name: '', code: '', city: '', uf: '', latitude: '', longitude: '', geofence_type: 'circle', geofence_polygon: null, geofence_radius_km: '', address: '', contact: '', link: '', notes: '', lodging_billing_mode: '', lodging_billing_notes: '', status: 'active', customer_uuid: '' },
  toForm: (p) => ({
    category: p.category?.code ?? '', name: p.name, code: p.code ?? '', city: p.city ?? '', uf: p.uf ?? '',
    latitude: p.latitude != null ? String(p.latitude) : '', longitude: p.longitude != null ? String(p.longitude) : '',
    geofence_type: p.geofence_type === 'polygon' ? 'polygon' : 'circle',
    geofence_polygon: p.geofence_polygon ?? null,
    geofence_radius_km: p.geofence_radius_km != null ? String(p.geofence_radius_km) : '',
    address: p.address ?? '', contact: p.contact ?? '', notes: p.notes ?? '',
    lodging_billing_mode: (p.lodging_billing_mode ?? '') as '' | 'semanal' | 'quinzenal' | 'mensal' | 'final',
    lodging_billing_notes: p.lodging_billing_notes ?? '',
    link: p.link ?? '',
    status: p.status === 'inactive' ? 'inactive' : 'active', customer_uuid: p.customer_uuid ?? '',
  }),
  toBody: (v) => ({
    category_code: v.category, name: v.name, code: v.code || null, city: v.city || null,
    uf: v.uf ? String(v.uf).toUpperCase() : null,
    latitude: parseCoord(v.latitude), longitude: parseCoord(v.longitude),
    geofence_type: v.geofence_type === 'polygon' ? 'polygon' : 'circle',
    // Polígono manda o anel; círculo zera o anel para não deixar dado morto.
    geofence_polygon: v.geofence_type === 'polygon' ? (v.geofence_polygon ?? null) : null,
    geofence_radius_km: v.geofence_radius_km === '' ? null : Number(v.geofence_radius_km),
    address: v.address || null, contact: v.contact || null, notes: v.notes || null,
    lodging_billing_mode: v.lodging_billing_mode || null,
    lodging_billing_notes: v.lodging_billing_notes || null,
    link: v.link || null,
    status: v.status, customer_uuid: v.customer_uuid || null,
  }),
}

// ── Frota ────────────────────────────────────────────────────────────
const FLEET_STATUS = [...ATIVO, { value: 'maintenance', label: 'Manutenção' }]
const OPCOES_DE_CLASSIFICACAO = CLASSIFICACOES_FROTA_LEVE.map((c) => ({
  value: c,
  label: ROTULO_DA_CLASSIFICACAO[c],
  icon: <ClassificacaoIcon classe={c} rotulo={ROTULO_DA_CLASSIFICACAO[c]} size={20} />,
}))
/** A classe que vale na grade: só nos tipos que recebem a Classificação. */
function classificacaoDaFrota(f: Fleet): ClassificacaoFrotaLeve | null {
  const classe = f.classificacao_frota_leve
  return ehClassificacao(classe) && CATEGORIAS_COM_CLASSIFICACAO.includes(f.category?.code ?? '') ? classe : null
}
const fleetSchema = z.object({
  category: z.string().min(1, 'Selecione o tipo do veículo'), name: z.string().optional(), code: z.string().optional(), filial: z.string().optional(),
  plate: z.string().optional(), brand: z.string().optional(), model: z.string().optional(), year: z.string().optional(),
  chassi: z.string().optional(),
  capacity: z.string().optional(), renavam: z.string().optional(), owner: z.string().optional(),
  // Eixos do veículo, para o esperado do pedágio (14/10/2026): de 1 a 12, ou vazio.
  eixos: z.string().optional().refine((v) => !v || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 12), 'Informe de 1 a 12 eixos'),
  // A classificação da frota leve no Mapa Operacional (20/10/2026). Do HUB, como os eixos.
  classificacao_frota_leve: z.string().optional(),
  on_leave: z.boolean(), status: z.enum(['active', 'inactive', 'maintenance']), notes: z.string().optional(),
  customer_uuid: z.string().optional(),
  // Só leitura: alimentam o cabeçalho de origem e a trava dos campos do ERP.
  protheus_code: z.string().optional(), protheus_tipo_veiculo: z.string().optional(),
  protheus_tipo_rodado: z.string().optional(), protheus_modalidade: z.string().optional(), protheus_situacao: z.string().optional(),
  protheus_reboque: z.string().optional(), protheus_synced_at: z.string().optional(),
})

function FleetFields() {
  const tt = useDeclarativeText()
  // A categoria É o tipo do veículo e define o ícone que representa essa placa em
  // todas as telas, então cada opção já mostra o desenho correspondente.
  const todasCategorias = useCategoryOptions(fleetCategoriesResource)
  // A linha é espelho do ERP: editar aqui duraria até o próximo ciclo, que
  // reescreve por cima. O backend também recusa a escrita destes campos, então
  // travar na tela é o aviso, não a proteção.
  const doProtheus = Boolean(useWatch({ name: 'protheus_code' }))
  const categoryOptions = todasCategorias
    // Cadastro do HUB escolhe entre os tipos manuais. Máquina entrou em 28/09/2026,
    // para a MAQ0097, que o ERP não publica.
    .filter((o) => doProtheus || o.value === 'plataforma' || o.value === 'maquina' || o.value === 'outros')
    .map((o) => ({
      ...o,
      icon: <FleetIcon category={{ uuid: o.value, code: o.value, label: o.label }} />,
    }))
  const tipoOrigem = useWatch({ name: 'protheus_tipo_veiculo' }) as string | undefined
  const rodadoOrigem = useWatch({ name: 'protheus_tipo_rodado' }) as string | undefined
  const modalidadeOrigem = useWatch({ name: 'protheus_modalidade' }) as string | undefined
  const situacaoOrigem = useWatch({ name: 'protheus_situacao' }) as string | undefined
  const reboqueOrigem = useWatch({ name: 'protheus_reboque' }) as string | undefined
  const syncedAt = useWatch({ name: 'protheus_synced_at' }) as string | undefined
  // Só a frota leve, a de fazenda e a de Outros recebem a Classificação, e só o
  // veículo classificado aparece na aba Frota Leve do Mapa (Samuel, 20/10/2026).
  const categoria = useWatch({ name: 'category' }) as string | undefined
  const classificavel = CATEGORIAS_COM_CLASSIFICACAO.includes(categoria ?? '')
  return (
    <>
      {doProtheus && (
        <OrigemProtheusCard
          syncedAt={syncedAt}
          itens={[
            { rotulo: 'Modalidade', valor: modalidadeOrigem || 'Não informada' },
            { rotulo: 'Tipo na origem', valor: tipoOrigem },
            { rotulo: 'Rodado', valor: rodadoOrigem },
            { rotulo: 'Acoplamento', valor: situacaoOrigem },
            { rotulo: 'Reboque', valor: reboqueOrigem },
          ]}
        />
      )}
      <FormSection title={tt('Identificação')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {/* Em veículo espelhado, o tipo é a Modalidade do Protheus. */}
          <RHFSelect name="category" label="Tipo do veículo" required options={categoryOptions} disabled={doProtheus} />
          <RHFText name="plate" label="Placa" disabled={doProtheus} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2 }}>
          <RHFText name="name" label="Nome" />
          <RHFText name="code" label="Código" />
          <RHFText name="filial" label="Filial" />
        </Box>
        {classificavel && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <RHFSelect
              name="classificacao_frota_leve"
              label="Classificação"
              options={OPCOES_DE_CLASSIFICACAO}
              helperText="Sem classificação, o veículo não aparece no Mapa Operacional"
            />
          </Box>
        )}
      </FormSection>
      <FormSection title={tt('Características')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          {/* Marca segue do HUB: o Protheus junta marca e modelo num campo só. */}
          <RHFText name="brand" label="Marca" />
          <RHFText name="model" label="Modelo" disabled={doProtheus} />
          <RHFText name="year" label="Ano" type="number" disabled={doProtheus} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 2 }}>
          <RHFText name="chassi" label="Chassi" disabled={doProtheus} />
          <RHFText name="capacity" label="Capacidade (t)" type="number" />
          {/* Do HUB mesmo em veículo do Protheus: o ERP não publica eixos. */}
          <RHFText name="eixos" label="Eixos" type="number" />
          <RHFText name="renavam" label="RENAVAM" />
        </Box>
      </FormSection>
      <FormSection title={tt('Complementos')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
          <RHFText name="owner" label="Proprietário" />
          <RHFSelect name="status" label="Situação" options={FLEET_STATUS} disabled={doProtheus} />
        </Box>
        <RHFSwitch name="on_leave" label="Em folga" />
        <RHFText name="notes" label="Observações" multiline rows={2} />
        <CustomerOwnerField />
      </FormSection>
    </>
  )
}

export const fleetConfig: ResourceConfig<Fleet> = {
  title: 'Frota', singular: 'item de frota', exportPrefix: 'frota',
  basePath: '/logistica/cadastros/fleet', labelOf: (f) => f.plate || f.name || f.code || '—', resource: fleetTodasDuplasResource,
  searchable: true, schema: fleetSchema, Fields: FleetFields, useFilters: makeCategoryFilter(fleetCategoriesResource),
  // O contador acompanha a modalidade escolhida (Samuel, 17/09/2026). A faixa
  // do Protheus continua falando da carga inteira; este número fala do filtro.
  //
  // "Resultado", e não "Veículos": a faixa logo acima já mostra "Veículos" com a
  // carga inteira, e dois números vizinhos com o mesmo rótulo e valores diferentes
  // é convite a ler o errado. Nas abas de Conjuntos o rótulo pode ser "Conjuntos"
  // porque ali não existe essa vizinhança.
  countLabel: 'Resultado',
  importEntity: 'fleet', protheusSync: ['frota'], Banner: FrotaBanner,
  createPermission: 'master_data.create', editPermission: 'master_data.edit', deletePermission: 'master_data.delete',
  columns: [
    {
      key: 'category',
      header: 'Tipo',
      render: (f) => {
        // Classificado, o veículo troca o desenho do tipo pelo da classe, o mesmo do
        // Mapa, e quem falta classificar fica à vista (Samuel, 20/10/2026). O nome da
        // classe vai embaixo porque o Apoio garagem usa o desenho do utilitário.
        const classe = classificacaoDaFrota(f)
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {classe
              ? <ClassificacaoIcon classe={classe} rotulo={ROTULO_DA_CLASSIFICACAO[classe]} sx={FLEET_ICON_SIZES.grid} />
              : <FleetIcon category={f.category} size="grid" />}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box component="span"><CategoryLabel category={f.category} /></Box>
              {classe && (
                <Box component="span" sx={{ fontSize: 12, lineHeight: 1.3, color: 'text.secondary' }}>
                  {ROTULO_DA_CLASSIFICACAO[classe]}
                </Box>
              )}
            </Box>
          </Box>
        )
      },
    },
    { key: 'plate', header: 'Placa', sortable: true },
    { key: 'name', header: 'Nome', sortable: true },
    { key: 'code', header: 'Matrícula', sortable: true },
    { key: 'filial', header: 'Filial', sortable: true },
    // Os eixos, para o esperado do pedágio (14/10/2026).
    { key: 'eixos', header: 'Eixos', render: (f) => (f.eixos != null ? String(f.eixos) : '—') },
    // Origem responde "posso editar esta linha", que é a pergunta que a grade
    // precisa responder sem abrir a ficha. Mesma coluna de Pessoas.
    {
      key: 'origem', header: 'Origem',
      render: (f) => {
        const dono = f.origem ?? (f.protheus_code ? 'Protheus' : 'HUB')
        return dono === 'Protheus'
          ? <StatusChip label="Protheus" tone="info" />
          : <StatusChip label="HUB" />
      },
    },
    { key: 'status', header: 'Situação', sortable: true, render: (f) => <CadastroStatusChip status={f.status} /> },
  ],
  emptyForm: { category: '', name: '', code: '', filial: '', plate: '', brand: '', model: '', year: '', chassi: '', capacity: '', eixos: '', classificacao_frota_leve: '', renavam: '', owner: '', on_leave: false, status: 'active', notes: '', customer_uuid: '', protheus_code: '', protheus_tipo_veiculo: '', protheus_tipo_rodado: '', protheus_modalidade: '', protheus_situacao: '', protheus_reboque: '', protheus_synced_at: '' },
  toForm: (f) => ({
    category: f.category?.code ?? '', name: f.name ?? '', code: f.code ?? '', filial: f.filial ?? '', plate: f.plate ?? '', brand: f.brand ?? '', model: f.model ?? '',
    year: f.year != null ? String(f.year) : '', chassi: f.chassi ?? '', capacity: f.capacity != null ? String(f.capacity) : '', eixos: f.eixos != null ? String(f.eixos) : '', classificacao_frota_leve: f.classificacao_frota_leve ?? '', renavam: f.renavam ?? '', owner: f.owner ?? '',
    on_leave: f.on_leave, status: f.status, notes: f.notes ?? '', customer_uuid: f.customer_uuid ?? '',
    protheus_code: f.protheus_code ?? '', protheus_tipo_veiculo: f.protheus_tipo_veiculo ?? '',
    protheus_tipo_rodado: f.protheus_tipo_rodado ?? '', protheus_modalidade: f.protheus_modalidade ?? '', protheus_situacao: f.protheus_situacao ?? '',
    protheus_reboque: f.protheus_reboque ?? '', protheus_synced_at: f.protheus_synced_at ?? '',
  }),
  toBody: (v) => {
    // Veículo espelhado manda só o que é do HUB. O servidor descarta o resto de
    // qualquer jeito; não enviar evita que a prévia do PATCH sugira o contrário.
    const doProtheus = Boolean(v.protheus_code)
    const doHub = {
      name: v.name || null, code: v.code || null, filial: v.filial || null, brand: v.brand || null,
      capacity: v.capacity === '' ? null : Number(v.capacity),
      eixos: v.eixos ? Number(v.eixos) : null,
      classificacao_frota_leve: CATEGORIAS_COM_CLASSIFICACAO.includes(v.category) ? (v.classificacao_frota_leve || null) : null,
      renavam: v.renavam || null, owner: v.owner || null, on_leave: v.on_leave,
      notes: v.notes || null, customer_uuid: v.customer_uuid || null,
    }
    if (doProtheus) return doHub
    return {
      ...doHub,
      category_code: v.category, plate: v.plate || null, model: v.model || null,
      year: v.year === '' ? null : Number(v.year), chassi: v.chassi || null, status: v.status,
    }
  },
}

// ── Pessoas ──────────────────────────────────────────────────────────
const PERSON_STATUS = [...ATIVO, { value: 'blocked', label: 'Bloqueado' }]
const EMPLOYMENT = [{ value: '', label: '—' }, { value: 'freelancer', label: 'Autônomo' }, { value: 'fleet', label: 'Frota' }]
const personnelSchema = z.object({
  category: z.string().min(1, 'Selecione a categoria'), name: z.string().min(1, 'Informe o nome'),
  document: z.string().optional(), phone: z.string().optional(), code: z.string().optional(), filial: z.string().optional(),
  cnh_number: z.string().optional(), cnh_category: z.string().optional(), cnh_expires_at: z.string().optional(),
  employment: z.string().optional(), carrier: z.string().optional(), city: z.string().optional(), uf: z.string().optional(),
  admissao: z.string().optional(), demissao: z.string().optional(),
  situacao_colaborador: z.string().optional(), situacao_colaborador_label: z.string().optional(),
  status: z.enum(['active', 'inactive', 'blocked']), notes: z.string().optional(), customer_uuid: z.string().optional(),
  // Função exercida no HUB, independente do cargo do Protheus. Editável mesmo em
  // pessoa espelhada: no ERP, manobra e prancheiro têm todos o cargo MOTORISTA.
  funcao_operacional: z.string().optional(),
  // A data em que a função nova passa a valer. Só viaja quando a função muda.
  funcao_vigente_de: z.string().optional(),
  // Espelho do valor salvo, para o campo de data aparecer só quando houve troca.
  funcao_operacional_inicial: z.string().optional(),
  // Não são campos editáveis: entram no formulário só para os controles saberem
  // que a linha é espelho do Protheus e que CPF e telefone vieram mascarados.
  protheus_code: z.string().optional(), protheus_operador_code: z.string().optional(),
  protheus_funcao: z.string().optional(),
  protheus_base: z.string().optional(), protheus_situacao: z.string().optional(),
  protheus_synced_at: z.string().optional(), pii_masked: z.boolean().optional(),
  // Diz se o município do espelho foi REALMENTE resolvido pela origem. Sem ele
  // não dá para distinguir cidade vinda do ERP de cidade que ninguém conseguiu
  // preencher, que são os dois casos que decidem a trava do campo.
  city_id: z.number().nullable().optional(),
})

/** Cabeçalho do formulário quando o registro veio do Protheus.
 *
 *  Existe para responder, antes de a pessoa tentar editar, por que metade dos
 *  campos está travada. Rótulo e valor em colunas, sem parêntese e sem travessão,
 *  pela convenção visual do projeto.
 *
 *  Serve pessoas e veículos: os campos de origem diferem, então quem chama monta a
 *  lista. O que é comum, e o que este card carrega, é a explicação da trava. */
function OrigemProtheusCard({ itens: recebidos, syncedAt }: {
  itens: { rotulo: string; valor?: string }[]; syncedAt?: string
}) {
  const tt = useDeclarativeText()
  const itens = [
    ...recebidos,
    { rotulo: 'Última sincronização', valor: syncedAt ? new Date(syncedAt).toLocaleString('pt-BR') : undefined },
  ].filter((i) => i.valor)
  return (
    <Card variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5, bgcolor: 'action.hover' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <StatusChip label="Protheus" tone="info" />
        <Typography variant="body2" color="text.secondary">
          {tt('Cadastro mantido no ERP. Os campos vindos de lá são reescritos a cada sincronização.')}
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
        {itens.map((i) => (
          <Box key={i.rotulo}>
            <Typography variant="caption" color="text.secondary" display="block">{tt(i.rotulo)}</Typography>
            <Typography variant="body2" fontWeight={600}>{i.valor}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  )
}

// Função operacional no HUB. As opções mudam com a CATEGORIA: só quem veio do
// cadastro de motoristas circula entre Motorista, Manobra e Prancheiro; operador é
// apenas operador e produtor não exerce nenhuma.
//
// A lista vem do servidor quando a ficha já existe, em `funcoes_disponiveis`. Este
// mapa responde pelo cadastro NOVO, onde ainda não há ficha para perguntar.
const FUNCAO_POR_CATEGORIA: Record<string, { value: string; label: string }[]> = {
  driver: [
    { value: 'motorista', label: 'Motorista' },
    { value: 'manobra', label: 'Manobra' },
    { value: 'prancheiro', label: 'Prancheiro' },
  ],
  operator: [{ value: 'operador', label: 'Operador' }],
}

/** Função operacional, com a data em que ela passa a valer.
 *
 *  A data só aparece depois que alguém TROCA a função: enquanto o valor é o
 *  salvo, pedir uma data seria pedir a confirmação de algo que não mudou. Ela pode
 *  ser no passado, que é como o cliente registra uma troca que já aconteceu, e
 *  nunca no futuro, o que o servidor também recusa. */
function FuncaoOperacionalField() {
  const tt = useDeclarativeText()
  const categoria = useWatch({ name: 'category' }) as string | undefined
  const atual = useWatch({ name: 'funcao_operacional' }) as string | undefined
  const salva = useWatch({ name: 'funcao_operacional_inicial' }) as string | undefined
  const opcoes = FUNCAO_POR_CATEGORIA[categoria ?? ''] ?? []
  const trocou = Boolean(salva) && Boolean(atual) && atual !== salva
  if (!opcoes.length) return null
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: trocou ? '2fr 1fr' : '1fr', gap: 2 }}>
      <RHFSelect
        name="funcao_operacional" label="Função operacional no HUB" required options={opcoes}
        helperText={tt('O cargo do Protheus não muda')}
      />
      {trocou && (
        <RHFDate
          name="funcao_vigente_de" label="Vigente desde"
          helperText={tt('Pode ser no passado')}
        />
      )}
    </Box>
  )
}

function PersonnelFields() {
  const categoryOptions = useCategoryOptions(personnelCategoriesResource)
  const tt = useDeclarativeText()
  const can = useCan()
  // A linha é espelho do ERP: editar aqui duraria até o próximo ciclo, que
  // reescreve por cima. O backend também recusa a escrita destes campos, então
  // travar na tela é o aviso, não a proteção.
  // Os DOIS códigos: motorista vem do getmotoristas e operador do getOperadores,
  // que são cadastros diferentes do ERP. Olhar só o primeiro deixaria a ficha do
  // operador editável na tela enquanto o servidor descarta a edição em silêncio.
  // Os dois `useWatch` em linhas próprias: dentro de um `||` o segundo ficaria
  // condicionado ao primeiro, e hook não pode ser condicional.
  const codigoMotorista = useWatch({ name: 'protheus_code' })
  const codigoOperador = useWatch({ name: 'protheus_operador_code' })
  const doProtheus = Boolean(codigoMotorista || codigoOperador)
  // `pii_masked` responde pelo registro carregado; a permissão responde pelo
  // criar, onde não há registro. Sem os dois, quem não pode ver CPF digitaria um
  // em cadastro novo e o servidor o descartaria em silêncio.
  const mascaraNoRegistro = Boolean(useWatch({ name: 'pii_masked' }))
  const piiMascarada = mascaraNoRegistro || !can('master_data.pii')
  // Município do espelho só fica travado quando a origem REALMENTE resolveu a
  // cidade. O ERP manda o nome cortado em 15 caracteres e sem UF, e o que não
  // resolve chega aqui vazio: travar nesse caso cria campo que ninguém preenche.
  // O servidor aplica a mesma exceção, em routes/personnel.py.
  const cidadeResolvida = Boolean(useWatch({ name: 'city_id' }))
  const municipioDoErp = doProtheus && cidadeResolvida
  const protheusFuncao = useWatch({ name: 'protheus_funcao' }) as string | undefined
  const protheusBase = useWatch({ name: 'protheus_base' }) as string | undefined
  const protheusSituacao = useWatch({ name: 'protheus_situacao' }) as string | undefined
  const protheusSyncedAt = useWatch({ name: 'protheus_synced_at' }) as string | undefined
  const avisoPii = piiMascarada ? 'Sem permissão para ver o valor completo' : undefined
  return (
    <>
      {doProtheus && (
        <OrigemProtheusCard
          syncedAt={protheusSyncedAt}
          itens={[
            { rotulo: 'Função na origem', valor: protheusFuncao },
            { rotulo: 'Base', valor: protheusBase },
            { rotulo: 'Situação na origem', valor: protheusSituacao },
          ]}
        />
      )}
      <FormSection title={tt('Identificação')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2 }}>
          <RHFSelect name="category" label="Categoria" required options={categoryOptions} />
          <RHFText name="code" label="Matrícula" disabled={doProtheus} />
          {/* A Filial fica na ficha quando sai das grades, em 28/09/2026: é o código
              que o arquivo de lançamentos do Protheus pede ao lado da matrícula, e
              a pessoa cadastrada no HUB só o tem se alguém digitar. A base não é
              campo do HUB, vem do ERP e aparece no quadro da origem, no alto. */}
          <RHFText name="filial" label="Filial" disabled={doProtheus} />
        </Box>
        <FuncaoOperacionalField />
        <RHFText name="name" label="Nome" required disabled={doProtheus} />
      </FormSection>
      <FormSection title={tt('Documentos')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <RHFText name="document" label="Documento (CPF)" disabled={doProtheus || piiMascarada} helperText={avisoPii} />
          <RHFText name="phone" label="Telefone" disabled={doProtheus || piiMascarada} helperText={avisoPii} />
        </Box>
        {/* CNH, vínculo e transportadora NÃO existem no Protheus: seguem do HUB e
            editáveis, inclusive numa pessoa espelhada. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          <RHFText name="cnh_number" label="CNH" />
          <RHFText name="cnh_category" label="Categoria CNH" />
          <RHFDate name="cnh_expires_at" label="Validade CNH" />
        </Box>
      </FormSection>
      <FormSection title={tt('Vínculo e local')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <RHFSelect name="employment" label="Vínculo" options={EMPLOYMENT} />
          <RHFText name="carrier" label="Transportadora" />
        </Box>
        {/* Admissão e demissão vêm do ERP e são SEMPRE só leitura, mesmo numa
            pessoa do HUB: o Protheus é a folha, e digitar aqui criaria uma segunda
            verdade que o próximo ciclo desfaz. Demissão vazia é quem está na ativa. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <RHFDate name="admissao" label="Admissão" disabled />
          <RHFDate name="demissao" label="Demissão" disabled />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
          <RHFText name="city" label="Município" disabled={municipioDoErp} />
          <RHFText name="uf" label="UF" disabled={municipioDoErp} />
        </Box>
      </FormSection>
      <FormSection title={tt('Complementos')}>
        {doProtheus ? (
          <RHFText
            name="situacao_colaborador_label" label="Situação" disabled
            helperText={tt('Derivada da situação, admissão e demissão no Protheus')}
          />
        ) : (
          <RHFSelect name="status" label="Situação" options={PERSON_STATUS} />
        )}
        <RHFText name="notes" label="Observações" multiline rows={2} />
        <CustomerOwnerField />
      </FormSection>
    </>
  )
}

/** Filtros da grade de Pessoas: CATEGORIA e FUNÇÃO, lado a lado.
 *
 *  Os dois porque são perguntas diferentes. Categoria responde "de que cadastro
 *  ela veio"; função responde "o que ela faz na operação". Um motorista, um
 *  manobra e um prancheiro são todos `driver`, e sem o segundo filtro não há como
 *  separá-los na tela. */
function usePessoasFilters(): ResourceFilters {
  const [category, setCategory] = useState('')
  const [funcao, setFuncao] = useState('')
  const tt = useDeclarativeText()
  const categorias = useCategoryOptions(personnelCategoriesResource)
  const funcoes = [
    { value: 'motorista', label: 'Motorista' },
    { value: 'manobra', label: 'Manobra' },
    { value: 'prancheiro', label: 'Prancheiro' },
    { value: 'operador', label: 'Operador' },
  ]
  return {
    params: { category: category || undefined, funcao: funcao || undefined },
    node: (
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small" exclusive value={category}
          onChange={(_, v) => setCategory(v ?? '')}
          aria-label={tt('Filtrar por categoria')}
        >
          <ToggleButton value="">{tt('Todas')}</ToggleButton>
          {categorias.map((o) => (
            <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small" exclusive value={funcao}
          onChange={(_, v) => setFuncao(v ?? '')}
          aria-label={tt('Filtrar por função operacional')}
        >
          <ToggleButton value="">{tt('Todas as funções')}</ToggleButton>
          {funcoes.map((o) => (
            <ToggleButton key={o.value} value={o.value}>{tt(o.label)}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    ),
  }
}

export const personnelConfig: ResourceConfig<Personnel> = {
  title: 'Pessoas', singular: 'pessoa', exportPrefix: 'pessoas',
  basePath: '/logistica/cadastros/personnel', labelOf: (p) => p.name, resource: personnelTodasDuplasResource, searchable: true,
  schema: personnelSchema, Fields: PersonnelFields, Banner: PessoasProtheusBanner,
  Below: FuncaoHistorico,
  protheusSync: ['motoristas', 'operadores'],
  useFilters: usePessoasFilters,
  categoryNewPath: '/logistica/categorias/personnel/new', importEntity: 'personnel',
  createPermission: 'master_data.create', editPermission: 'master_data.edit', deletePermission: 'master_data.delete',
  columns: [
    { key: 'name', header: 'Nome', sortable: true },
    { key: 'category', header: 'Categoria', render: (p) => <CategoryLabel category={p.category} /> },
    // Função ao lado da Categoria: são coisas diferentes, e é justamente a
    // diferença entre as duas que o cliente pediu para conseguir enxergar.
    { key: 'funcao_operacional', header: 'Função', sortable: true, render: (p) => p.funcao_operacional_label ?? '—' },
    { key: 'document', header: 'Documento', sortable: true },
    { key: 'phone', header: 'Telefone', sortable: true },
    { key: 'code', header: 'Matrícula', sortable: true },
    // A filial saiu da grade em 28/09/2026 e a Base, que já vinha ao lado, ficou no
    // lugar: "todo lugar que tiver filial pode substituir por base" (Samuel). O
    // código continua no começo da Matrícula, que é a composta, e na ficha. A base
    // sai sem o prefixo da empresa, como nas outras grades.
    { key: 'protheus_base', header: 'Base', sortable: true, render: (p) => semPrefixoDaEmpresa(p.protheus_base) ?? '—' },
    // Origem responde "posso editar esta linha", que é a pergunta que a grade
    // precisa responder sem abrir a ficha.
    {
      key: 'origem', header: 'Origem',
      // O servidor manda `origem` pronta. Recuo em protheus_code para não depender
      // da ordem de implantação entre backend e frontend.
      render: (p) => {
        const dono = p.origem ?? ((p.protheus_code || p.protheus_operador_code) ? 'Protheus' : 'HUB')
        return dono === 'Protheus'
          ? <StatusChip label="Protheus" tone="info" />
          : <StatusChip label="HUB" />
      },
    },
    {
      // Situação, e há quanto tempo ela vale. A idade fica numa linha própria
      // abaixo do chip porque é dado de apoio, não rótulo: quem varre a coluna lê
      // primeiro a situação e só desce quando ela não é Ativo.
      //
      // Ela existe porque o ERP não baixa a situação sozinho e o endpoint não manda
      // período de ausência nenhum. Sem a idade, "de férias" de ontem e de meses
      // atrás são a mesma linha, e é a segunda que precisa ser cobrada lá.
      key: 'status', header: 'Situação', sortable: true,
      render: (p) => {
        const situacao = p.situacao_colaborador ?? p.status
        const dias = p.situacao_ha_dias
        const mostrarIdade = situacao !== 'ativo' && typeof dias === 'number' && dias > 0
        return (
          <Box>
            <CadastroStatusChip status={situacao} />
            {mostrarIdade && (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
              >
                há {dias} {dias === 1 ? 'dia' : 'dias'}
              </Typography>
            )}
          </Box>
        )
      },
    },
  ],
  emptyForm: { category: '', funcao_operacional: '', funcao_vigente_de: '', funcao_operacional_inicial: '', name: '', document: '', phone: '', code: '', filial: '', cnh_number: '', cnh_category: '', cnh_expires_at: '', employment: '', carrier: '', city: '', uf: '', admissao: '', demissao: '', status: 'active', situacao_colaborador: '', situacao_colaborador_label: '', notes: '', customer_uuid: '', protheus_code: '', protheus_operador_code: '', protheus_funcao: '', protheus_base: '', protheus_situacao: '', protheus_synced_at: '', pii_masked: false, city_id: null },
  toForm: (p) => ({
    category: p.category?.code ?? '',
    funcao_operacional: p.funcao_operacional ?? '', funcao_vigente_de: '',
    funcao_operacional_inicial: p.funcao_operacional ?? '',
    name: p.name, document: p.document ?? '', phone: p.phone ?? '', code: p.code ?? '', filial: p.filial ?? '',
    cnh_number: p.cnh_number ?? '', cnh_category: p.cnh_category ?? '', cnh_expires_at: p.cnh_expires_at ?? '',
    employment: p.employment ?? '', carrier: p.carrier ?? '', city: p.city ?? '', uf: p.uf ?? '',
    admissao: p.admissao ?? '', demissao: p.demissao ?? '', status: p.status,
    situacao_colaborador: p.situacao_colaborador ?? '',
    situacao_colaborador_label: p.situacao_colaborador_label ?? p.status_label ?? '',
    notes: p.notes ?? '', customer_uuid: p.customer_uuid ?? '',
    protheus_code: p.protheus_code ?? '', protheus_operador_code: p.protheus_operador_code ?? '',
    protheus_funcao: p.protheus_funcao ?? '', protheus_base: p.protheus_base ?? '',
    protheus_situacao: p.protheus_situacao ?? '', protheus_synced_at: p.protheus_synced_at ?? '', pii_masked: p.pii_masked ?? false,
    city_id: p.city_id ?? null,
  }),
  toBody: (v) => {
    // Os dois cadastros do ERP, pela mesma razão do formulário: o operador é
    // espelho tanto quanto o motorista, e o servidor recusa a escrita dos dois.
    const doProtheus = Boolean(v.protheus_code || v.protheus_operador_code)
    const body: Record<string, unknown> = {
      category_code: v.category,
      cnh_number: v.cnh_number || null, cnh_category: v.cnh_category || null, cnh_expires_at: v.cnh_expires_at || null,
      employment: v.employment || null, carrier: v.carrier || null,
      notes: v.notes || null, customer_uuid: v.customer_uuid || null,
    }
    // A função vai JUNTO com o espelho do Protheus, ao contrário de tudo que o ERP
    // mantém: é campo do HUB, e classificar como Manobra quem o ERP cadastrou como
    // MOTORISTA é o requisito inteiro. A data só viaja quando houve troca.
    if (v.funcao_operacional) {
      body.funcao_operacional = v.funcao_operacional
      if (v.funcao_vigente_de && v.funcao_operacional !== v.funcao_operacional_inicial) {
        body.funcao_vigente_de = v.funcao_vigente_de
      }
    }
    // Campos mantidos no ERP não são reenviados: o backend já os ignora numa
    // linha espelhada, e mandá-los faria a tela parecer estar salvando algo.
    if (!doProtheus) {
      Object.assign(body, {
        name: v.name, code: v.code || null, filial: v.filial || null,
        city: v.city || null, uf: v.uf ? String(v.uf).toUpperCase() : null,
        status: v.status,
      })
    }
    // Valor mascarado nunca volta: gravaria a máscara por cima do CPF real.
    if (!doProtheus && !v.pii_masked) {
      Object.assign(body, { document: v.document || null, phone: v.phone || null })
    }
    return body
  },
}

// ── Ativos (produtos) ────────────────────────────────────────────────
const assetSchema = z.object({
  category: z.string().min(1, 'Selecione a categoria'), name: z.string().min(1, 'Informe o nome'),
  unit: z.enum(['ton', 'sacas', 'kg']), status: z.enum(['active', 'inactive']), customer_uuid: z.string().optional(),
})

function AssetFields() {
  const categoryOptions = useCategoryOptions(assetCategoriesResource)
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <RHFSelect name="category" label="Categoria" required options={categoryOptions} />
        <RHFText name="name" label="Nome" required placeholder="Soja, milho…" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <RHFSelect name="unit" label="Unidade" options={[{ value: 'ton', label: 'Toneladas' }, { value: 'sacas', label: 'Sacas' }, { value: 'kg', label: 'Quilos' }]} />
        <RHFSelect name="status" label="Situação" options={ATIVO} />
      </Box>
      <CustomerOwnerField />
    </>
  )
}

const UNIT_LABELS: Record<string, string> = { ton: 'Toneladas', sacas: 'Sacas', kg: 'Quilos' }

export const assetsConfig: ResourceConfig<Asset> = {
  title: 'Produtos', singular: 'produto', exportPrefix: 'produtos',
  basePath: '/logistica/cadastros/assets', labelOf: (a) => a.name, resource: assetsResource, searchable: true,
  schema: assetSchema, Fields: AssetFields, categoryNewPath: '/logistica/categorias/assets/new',
  createPermission: 'master_data.create', editPermission: 'master_data.edit', deletePermission: 'master_data.delete',
  columns: [
    { key: 'name', header: 'Produto', sortable: true },
    { key: 'category', header: 'Categoria', render: (a) => <CategoryLabel category={a.category} /> },
    { key: 'unit', header: 'Unidade', sortable: true, render: (a) => <DeclarativeText value={UNIT_LABELS[a.unit] ?? a.unit} /> },
    { key: 'status', header: 'Situação', sortable: true, render: (a) => <CadastroStatusChip status={a.status} /> },
  ],
  emptyForm: { category: '', name: '', unit: 'ton', status: 'active', customer_uuid: '' },
  toForm: (a) => ({ category: a.category?.code ?? '', name: a.name, unit: a.unit, status: a.status === 'inactive' ? 'inactive' : 'active', customer_uuid: a.customer_uuid ?? '' }),
  toBody: (v) => ({ category_code: v.category, name: v.name, unit: v.unit, status: v.status, customer_uuid: v.customer_uuid || null }),
}

// ── Feriados ─────────────────────────────────────────────────────────
// Não é enfeite de calendário. A data alimenta a régua de hora extra do Painel de
// Jornada, onde feriado vale como domingo e leva a jornada inteira daquele dia a
// 100%. Gravar ou apagar recalcula o dia no servidor, inclusive em jornada já
// fechada, e é por isso que o cadastro é curto de propósito: cada linha é uma
// decisão de apuração, não um rótulo.

const feriadoSchema = z.object({
  dia: z.string().min(1, 'Informe a data'),
  nome: z.string().min(1, 'Informe o nome do feriado'),
})

function FeriadoFields() {
  return (
    <>
      <RHFDate name="dia" label="Data" required />
      <RHFText name="nome" label="Nome" required />
    </>
  )
}

/** Que dia da semana a data cai, para quem cadastra ver na hora que um feriado em
 *  domingo não muda nada: domingo já é 100% por si só. */
function DiaDaSemana({ dia }: { dia: string }) {
  const m = dia.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return <>—</>
  const data = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  const rotulo = data.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' })
  return <>{rotulo.charAt(0).toUpperCase() + rotulo.slice(1)}</>
}

export const feriadosConfig: ResourceConfig<Feriado> = {
  title: 'Feriados', singular: 'feriado', exportPrefix: 'feriados',
  basePath: '/logistica/cadastros/feriados', labelOf: (f) => f.nome,
  resource: feriadosResource, searchable: true,
  schema: feriadoSchema, Fields: FeriadoFields, formMaxWidth: 480,
  createPermission: 'master_data.create',
  editPermission: 'master_data.edit',
  deletePermission: 'master_data.delete',
  columns: [
    { key: 'dia', header: 'Data', sortable: true, render: (f) => fmtDate(f.dia) },
    { key: 'semana', header: 'Dia da semana', render: (f) => <DiaDaSemana dia={f.dia} /> },
    { key: 'nome', header: 'Feriado', sortable: true },
  ],
  emptyForm: { dia: '', nome: '' },
  toForm: (f) => ({ dia: f.dia, nome: f.nome }),
  toBody: (v) => ({ dia: v.dia, nome: v.nome }),
}

// ── Usuários (equipe + cliente) ──────────────────────────────────────
// O FORMULÁRIO vive em features/usuarios/UsuarioFormPage: seis abas de vinculação
// não cabem na casca genérica de um cartão só. Este config responde pela LISTA e
// reaproveita o mesmo modelo da tela, para os dois nunca divergirem.

/** O ResourceConfig exige `Fields`. Apontar para a seção real da tela, em vez de
 *  um esqueleto morto, mantém o tipo satisfeito com código vivo. */
const UserFields = ({ editing }: { editing: boolean }) => (
  <IdentidadeSection editando={editing} variante="backoffice" />
)

/** Papel do usuário na coluna da lista. Lê as MESMAS chaves da tela de edição:
 *  os rótulos antigos traziam o tipo entre parênteses, que a convenção visual do
 *  projeto não aceita, e as duas telas mostravam textos diferentes. */
function PapelDoUsuario({ role }: { role: Role }) {
  const { t } = useI18n()
  return <>{t(`usuarios.form.role.${role}`)}</>
}

function useScopeFilter(): ResourceFilters {
  const [scope, setScope] = useState('')
  const tt = useDeclarativeText()
  return {
    params: { scope: scope || undefined },
    node: (
      <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => setScope(v ?? '')} aria-label={tt('Filtrar por tipo')}>
        <ToggleButton value="">{tt('Todos')}</ToggleButton>
        <ToggleButton value="staff">{tt('Equipe')}</ToggleButton>
        <ToggleButton value="customer">{tt('Cliente')}</ToggleButton>
      </ToggleButtonGroup>
    ),
  }
}

export const usuariosConfig: ResourceConfig<Usuario> = {
  title: 'Usuários',
  singular: 'usuário', basePath: '/administrativo/usuarios', labelOf: (u) => u.name, resource: usersResource,
  exportPrefix: 'usuarios',
  searchable: true, schema: usuarioSchema, Fields: UserFields, useFilters: useScopeFilter,
  createPermission: 'users.manage', editPermission: 'users.manage', deletePermission: 'users.manage',
  columns: [
    { key: 'name', header: 'Nome', sortable: true },
    { key: 'email', header: 'E-mail', sortable: true },
    { key: 'role', header: 'Papel', sortable: true, render: (u) => <PapelDoUsuario role={u.role} /> },
    { key: 'cargo', header: 'Cargo', render: (u) => u.cargo?.nome ?? '—' },
    { key: 'is_staff', header: 'Tipo', render: (u) => <StatusChip label={u.is_staff ? 'Equipe' : 'Cliente'} tone={u.is_staff ? 'primary' : 'info'} /> },
    { key: 'customer_nome', header: 'Cliente', render: (u) => u.customer_nome ?? '—' },
    {
      key: 'is_active', header: 'Acesso',
      // "Aguardando 1º acesso" substituiu o antigo "Pendente": com a senha
      // temporária, a conta nasce com acesso liberado, e o que fica pendente é a
      // pessoa entrar e criar a senha dela.
      render: (u) =>
        u.is_blocked ? <StatusChip label="Bloqueado" tone="error" />
        : !u.is_active ? <StatusChip label="Inativo" tone="neutral" />
        : u.primeiro_acesso_pendente ? <StatusChip label="Aguardando 1º acesso" tone="warning" />
        : <StatusChip label="Ativo" tone="success" />,
    },
  ],
  emptyForm: usuarioVazio('backoffice'),
  toForm: usuarioParaForm,
  toBody: (v) => usuarioParaBody(v as UsuarioFormValues, 'backoffice'),
}

// ── Cargos (Samuel, 15/10/2026) ──────────────────────────────────────
// "Lá em usuários, ter um campo de criar cargos. [...] Se eu precisar fazer uma
// alteração, eu vou lá e altero no cargo e já vale para todos os outros." O operador
// da equipe com cargo herda estas permissões; o cargo dele se escolhe na aba
// Permissões do usuário. Mesma pasta de permissões da tela de Usuários.

const cargoSchema = z.object({
  nome: z.string().trim().min(1, 'Informe o nome do cargo').max(80, 'O nome do cargo vai até 80 caracteres'),
  areas: z.array(z.string()).default([]),
  permissions: z.array(z.string()).default([]),
})

function CargoFields() {
  const tt = useDeclarativeText()
  const { t } = useI18n()
  // As áreas vêm antes das permissões (24/10/2026): o seletor de permissões
  // desabilita os módulos das áreas não marcadas e limpa o que estava neles.
  const areas = useWatch({ name: 'areas' }) as string[] | undefined
  return (
    <>
      <RHFText name="nome" label="Nome" required />
      <RHFAreaPicker name="areas" ajuda={t('form.areaPicker.ajudaCargo')} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {tt('Todo usuário deste cargo recebe estas permissões. Mudar aqui muda todos eles de uma vez.')}
      </Typography>
      <RHFPermissionPicker name="permissions" side="staff" areasDoAlvo={areas ?? []} />
    </>
  )
}

/** As áreas do cargo na lista, na ordem do catálogo. */
function AreasDoCargo({ areas }: { areas: string[] }) {
  const { t } = useI18n()
  const nomes = AREAS.filter((a) => areas.includes(a.key)).map((a) => t(a.labelKey))
  return <>{nomes.length ? nomes.join(', ') : '—'}</>
}

export const cargosConfig: ResourceConfig<Cargo> = {
  title: 'Cargos', singular: 'cargo',
  basePath: '/administrativo/cargos', labelOf: (c) => c.nome,
  resource: cargosResource, searchable: true,
  schema: cargoSchema, Fields: CargoFields,
  createPermission: 'users.manage',
  editPermission: 'users.manage',
  deletePermission: 'users.manage',
  columns: [
    { key: 'nome', header: 'Cargo', sortable: true },
    { key: 'areas', header: 'Áreas', render: (c) => <AreasDoCargo areas={c.areas ?? []} /> },
    { key: 'permissions', header: 'Permissões', render: (c) => fmtNum(c.permissions.length) },
    { key: 'usuarios', header: 'Usuários', render: (c) => fmtNum(c.usuarios ?? 0) },
  ],
  emptyForm: { nome: '', areas: [], permissions: [] },
  toForm: (c) => ({ nome: c.nome, areas: c.areas ?? [], permissions: c.permissions }),
  toBody: (v) => ({ nome: v.nome, areas: v.areas ?? [], permissions: v.permissions ?? [] }),
}
